import { useMemo } from 'react'
import {
  AddOutlined as AddOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon
} from '@mui/icons-material'
import { getBooleanLabel, getStatusLabel } from '../../../../../../../constants/inventoryLabels'
import Badge from '../../components/Badge'
import DataTable from '../../components/DataTable'
import SectionCard from '../../components/SectionCard'
import { isProtectedWarehouse, toBoolean } from '../../utils/inventoryResolvers'

const WarehousesSection = ({ actions, data, state }) => {
  const filteredWarehouses = useMemo(() => {
    const normalizedSearch = state.warehouseSearch.trim().toLowerCase()
    if (!normalizedSearch) return data.visibleWarehouses

    return data.visibleWarehouses.filter(row => {
      const haystack = `${row?.code ?? row?.codigo ?? ''} ${row?.name ?? row?.nombre ?? ''}`.toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [data.visibleWarehouses, state.warehouseSearch])

  return (
    <SectionCard
      title='Bodegas'
      description='Explora las bodegas registradas y abre un modal para crear una nueva sin mezclar el formulario con la tabla.'
    >
      <div className='space-y-4'>
        <div className='flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 lg:flex-row lg:items-end lg:justify-between'>
          <div className='w-full lg:max-w-xl'>
            <label className='mb-1 block text-xs font-semibold text-slate-700'>Buscar bodega</label>
            <input
              type='text'
              value={state.warehouseSearch}
              onChange={event => state.setWarehouseSearch(event.target.value)}
              className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              placeholder='Buscar por código o nombre'
            />
          </div>

          <button
            type='button'
            onClick={actions.handleOpenCreateWarehouseModal}
            className='inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent2 px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent2/90'
          >
            <AddOutlinedIcon style={{ fontSize: '1rem' }} />
            Crear bodega
          </button>
        </div>

        <DataTable
          columns={['Código', 'Nombre', 'Predeterminada', 'Estado', 'Acciones']}
          rows={filteredWarehouses}
          loading={data.warehousesQuery.isLoading || data.isLoadingAny}
          emptyText='No hay bodegas para esta búsqueda.'
          renderRow={row => {
            const isActive = toBoolean(row?.is_active ?? row?.isActive ?? row?.active ?? true)
            const isDefault = toBoolean(row?.is_default ?? row?.isDefault ?? row?.default)

            return (
              <>
                <td className='px-4 py-3 align-top font-semibold text-slate-900'>
                  {row?.code ?? row?.codigo ?? '-'}
                </td>
                <td className='px-4 py-3 align-top text-slate-700'>{row?.name ?? row?.nombre ?? '-'}</td>
                <td className='px-4 py-3 align-top text-slate-700'>{getBooleanLabel(isDefault)}</td>
                <td className='px-4 py-3 align-top'>
                  <Badge text={getStatusLabel(isActive)} tone={isActive ? 'green' : 'red'} />
                </td>
                <td className='px-4 py-3 align-top'>
                  <div className='flex justify-end gap-2'>
                    <button
                      type='button'
                      onClick={() => actions.handleOpenEditWarehouseModal(row)}
                      className='inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50'
                    >
                      <EditOutlinedIcon style={{ fontSize: '0.9rem' }} />
                      Editar
                    </button>

                    {isProtectedWarehouse(row) ? (
                      <span className='inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-500'>
                        Protegida
                      </span>
                    ) : (
                      <button
                        type='button'
                        onClick={() => actions.handleRequestDeleteWarehouse(row)}
                        disabled={actions.deleteWarehouseMutation.isPending}
                        className='inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition hover:border-red-300 hover:bg-red-50 disabled:opacity-60'
                      >
                        <DeleteOutlineOutlinedIcon style={{ fontSize: '0.9rem' }} />
                        Eliminar
                      </button>
                    )}
                  </div>
                </td>
              </>
            )
          }}
        />
      </div>
    </SectionCard>
  )
}

export default WarehousesSection
